import React from 'react'
import PropTypes from 'prop-types'
import Grow from '../Grow'
import * as Component from './Tooltip.styles'

import {
  useForkRef,
  useTheme,
  useId,
  useIsFocusVisible,
  useControlled,
} from 'hooks'
import { deepmerge } from 'settings/app/helpers'
import { elementAcceptingRef } from 'settings/propTypeDefinitions'
import { __dev__ } from 'settings/app/constants'

const popperStyles = {
  popperInteractive: {
    pointerEvents: 'auto',
  },
}

let hystersisOpen = false
let hystersisTimer = null

const Tooltip = React.forwardRef((props, ref) => {
  const {
    arrow = false,
    children,
    className,
    disableFocusListener = false,
    disableHoverListener = false,
    disableTouchListener = false,
    enterDelay = 100,
    enterNextDelay = 0,
    enterTouchDelay = 700,
    id: idProp,
    interactive = false,
    leaveDelay = 0,
    leaveTouchDelay = 1500,
    onClose,
    onOpen,
    open: openProp,
    placement = 'bottom',
    PopperComponent = Component.PopperComponent,
    PopperProps,
    title,
    TransitionComponent = Grow,
    TransitionProps,
    ...other
  } = props
  const theme = useTheme()

  const [childNode, setChildNode] = React.useState()
  const [arrowRef, setArrowRef] = React.useState(null)
  const ignoreTouchEvents = React.useRef(false)

  const closeTimer = React.useRef()
  const enterTimer = React.useRef()
  const leaveTimer = React.useRef()
  const touchTimer = React.useRef()
  const [openState, setOpenState] = useControlled({
    controlled: openProp,
    default: false,
    name: 'Tooltip',
    state: 'open',
  })

  let open = openState

  if (__dev__) {
    // eslint-disable-next-line
    const { current: isControlled } = React.useRef(openProp !== undefined)

    // eslint-disable-next-line
    React.useEffect(() => {
      if (
        childNode &&
        childNode.disabled &&
        !isControlled &&
        title !== '' &&
        childNode.tagName.toLowerCase() === 'button'
      ) {
        console.error(
          [
            'You are providing a disabled `button` child to the Tooltip component.',
            'A disabled element does not fire events.',
            `Tooltip needs to listen to the child element's events to display the title.`,
            '',
            'Add a simple wrapper element, such as a `span`.',
          ].join('\n')
        )
      }
    }, [title, childNode, isControlled])
  }

  const id = useId(idProp)

  React.useEffect(
    () => () => {
      clearTimeout(closeTimer.current)
      clearTimeout(enterTimer.current)
      clearTimeout(leaveTimer.current)
      clearTimeout(touchTimer.current)
    },
    []
  )

  const handleOpen = (event) => {
    clearTimeout(hystersisTimer)
    hystersisOpen = true

    // The mouseover event will trigger for every nested element in the tooltip.
    // We can skip rerendering when the tooltip is already open.
    // We are using the mouseover event instead of mouseenter event to fix a hide/show issue.
    setOpenState(true)

    if (onOpen) {
      onOpen(event)
    }
  }

  const handleEnter =
    (forward = true) =>
    (event) => {
      const childrenProps = children.props

      if (event.type === 'mouseover' && childrenProps.onMouseOver && forward) {
        childrenProps.onMouseOver(event)
      }

      if (ignoreTouchEvents.current && event.type !== 'touchstart') {
        return
      }

      // Remove the title ahead of time.
      // We don't want to wait for the next render commit.
      // We would risk displaying two tooltips at the same time (native + this one)
      if (childNode) {
        childNode.removeAttribute('title')
      }

      clearTimeout(enterTimer.current)
      clearTimeout(leaveTimer.current)
      if (enterDelay || (hystersisOpen && enterNextDelay)) {
        event.persist()
        enterTimer.current = setTimeout(
          () => {
            handleOpen(event)
          },
          hystersisOpen ? enterNextDelay : enterDelay
        )
      } else {
        handleOpen(event)
      }
    }

  const {
    isFocusVisible,
    onBlurVisible,
    ref: focusVisibleRef,
  } = useIsFocusVisible()
  const [childIsFocusVisible, setChildIsFocusVisible] = React.useState(false)
  const handleBlur = () => {
    if (childIsFocusVisible) {
      setChildIsFocusVisible(false)
      onBlurVisible()
    }
  }

  const handleFocus =
    (forward = true) =>
    (event) => {
      // Workaround for https://github.com/facebook/react/issues/7769
      // The autoFocus of React might trigger the event before the componentDidMount
      // We need to account for this eventuallity.
      if (!childNode) {
        setChildNode(event.currentTarget)
      }

      if (isFocusVisible(event)) {
        setChildIsFocusVisible(true)
        handleEnter()(event)
      }

      const childrenProps = children.props
      if (childrenProps.onFocus && forward) {
        childrenProps.onFocus(event)
      }
    }

  const handleClose = (event) => {
    clearTimeout(hystersisTimer)
    hystersisTimer = setTimeout(() => {
      hystersisOpen = false
    }, 800 + leaveDelay)
    setOpenState(false)

    if (onClose) {
      onClose(event)
    }

    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => {
      ignoreTouchEvents.current = false
    }, theme.transitions.duration.shortest)
  }

  const handleLeave =
    (forward = true) =>
    (event) => {
      const childrenProps = children.props

      if (event.type === 'blur') {
        if (childrenProps.onBlur && forward) {
          childrenProps.onBlur(event)
        }
        handleBlur()
      }

      if (
        event.type === 'mouseleave' &&
        childrenProps.onMouseLeave &&
        event.currentTarget === childNode
      ) {
        childrenProps.onMouseLeave(event)
      }

      clearTimeout(enterTimer.current)
      clearTimeout(leaveTimer.current)
      event.persist()
      leaveTimer.current = setTimeout(() => {
        handleClose(event)
      }, leaveDelay)
    }

  const detectTouchStart = (event) => {
    ignoreTouchEvents.current = true

    const childrenProps = children.props
    if (childrenProps.onTouchStart) {
      childrenProps.onTouchStart(event)
    }
  }

  const handleTouchStart = (event) => {
    detectTouchStart(event)
    clearTimeout(leaveTimer.current)
    clearTimeout(closeTimer.current)
    clearTimeout(touchTimer.current)
    event.persist()
    touchTimer.current = setTimeout(() => {
      handleEnter()(event)
    }, enterTouchDelay)
  }

  const handleTouchEnd = (event) => {
    if (children.props.onTouchedEnd) {
      children.props.onTouchEnd(event)
    }

    clearTimeout(touchTimer.current)
    clearTimeout(leaveTimer.current)
    event.persist()
    leaveTimer.current = setTimeout(() => {
      handleClose(event)
    }, leaveTouchDelay)
  }

  const handleUseRef = useForkRef(setChildNode, ref)
  const handleFocusRef = useForkRef(focusVisibleRef, handleUseRef)
  const handleRef = useForkRef(children.ref, handleFocusRef)

  // // There is no point in displaying an empty tooltip.
  if (title === '') {
    open = false
  }

  // // For accessibility and SEO concerns, we render the title to the DOM node when
  // // the tooltip is hidden. However, we made a tradeoff when
  // // `disableHoverListener` is set. This title logic is disabled.
  // // It's allowing us to keep the implementation size minimal.
  // // We are open to change the tradeoff.
  const shouldShowNativeTitle = !open && !disableHoverListener
  const childrenProps = {
    'aria-describedby': open ? id : null,
    title: shouldShowNativeTitle && typeof title === 'string' ? title : null,
    ...other,
    ...children.props,
    className: children.props.className,
    onTouchStart: detectTouchStart,
    ref: handleRef,
  }

  const interactiveWrapperListeners = {}

  if (!disableTouchListener) {
    childrenProps.onTouchStart = handleTouchStart
    childrenProps.onTouchEnd = handleTouchEnd
  }

  if (!disableHoverListener) {
    childrenProps.onMouseOver = handleEnter()
    childrenProps.onMouseLeave = handleLeave()

    if (interactive) {
      interactiveWrapperListeners.onMouseOver = handleEnter(false)
      interactiveWrapperListeners.onMouseLeave = handleLeave(false)
    }
  }

  if (!disableFocusListener) {
    childrenProps.onFocus = handleFocus()
    childrenProps.onBlur = handleLeave()

    if (interactive) {
      interactiveWrapperListeners.onFocus = handleFocus(false)
      interactiveWrapperListeners.onBlur = handleLeave(false)
    }
  }

  if (__dev__) {
    if (children.props.title) {
      console.error(
        [
          `You have provided a \`title\` prop to the child of <Tooltip />.`,
          `Remove this title prop \`${ children.props.title }\` or the Tooltip component.`,
        ].join('\n')
      )
    }
  }

  const mergedPopperProps = React.useMemo(
    () =>
      deepmerge(
        {
          popperOptions: {
            modifiers: [
              {
                name: 'arrow',
                options: {
                  element: arrowRef,
                },
              },
              {
                name: 'offset',
                options: {
                  offset: ({ placement }) => {
                    if (placement === 'bottom-start') {
                      return [-10, 8]
                    } else if (placement === 'bottom-end') {
                      return [10, 8]
                    }
                    return [0, 8]
                  },
                },
              },
              {
                name: 'flip',
                options: {
                  flipVariations: false,
                  fallbackPlacements: ['bottom'],
                  allowedAutoPlacements: ['bottom'],
                },
              },
            ],
          },
        },
        PopperProps
      ),
    [arrowRef, PopperProps]
  )

  return (
    <>
      {React.cloneElement(children, childrenProps)}
      <PopperComponent
        placement={ placement }
        anchorEl={ childNode }
        open={ childNode ? open : false }
        id={ childrenProps['aria-describedby'] }
        transition
        { ...interactiveWrapperListeners }
        { ...mergedPopperProps }
        style={{
          ...(interactive && popperStyles.popperInteractive),
        }}>
        {({
          placement: placementInner,
          TransitionProps: TransitionPropsInner,
        }) => (
          <TransitionComponent
            timeout={ theme.transitions.duration.shorter }
            { ...TransitionPropsInner }
            { ...TransitionProps }>
            <Component.Tooltip
              placement={ placementInner.split('-')[0] }
              arrow={ arrow }
              className={ className }>
              <Component.TooltipInner>{title}</Component.TooltipInner>
              {arrow ? (
                <Component.Arrow ref={ setArrowRef }>
                  <Component.ArrowSvg width='16' height='8'>
                    <polygon
                      points='0,8 16,8 8,0'
                      style={{
                        fill: '#008a34',
                      }}
                    />
                    <polygon
                      points='1,8 15,8 8,1'
                      style={{
                        fill: 'white',
                      }}
                    />
                  </Component.ArrowSvg>
                </Component.Arrow>
              ) : null}
            </Component.Tooltip>
          </TransitionComponent>
        )}
      </PopperComponent>
    </>
  )
})

Tooltip.propTypes = {
  // If `true`, adds an arrow to the tooltip
  arrow: PropTypes.bool,
  // Tooltip reference element
  children: elementAcceptingRef.isRequired,
  className: PropTypes.string,
  // Do not respond to focus events
  disableFocusListener: PropTypes.bool,
  // Do not respond to hover events
  disableHoverListener: PropTypes.bool,
  // Do not respond to long press touch events
  disableTouchListener: PropTypes.bool,
  /**
   * The number of milliseconds to wait before showing the tooltip.
   * This prop won't impact the enter touch delay (`enterTouchDelay`).
   */
  enterDelay: PropTypes.number,
  // The number of milliseconds to wait before showing the tooltip when one was already recently opened.
  enterNextDelay: PropTypes.number,
  // The number of milliseconds a user must touch the element before showing the tooltip.
  enterTouchDelay: PropTypes.number,
  /**
   * This prop is used to help implement accessibility logic.
   * If you don't provide this prop. It falls back to a randomly generated id.
   */
  id: PropTypes.string,
  /** Makes a tooltip interactive, i.e will not close when the user
   * hovers over the tooltip before the `leaveDelay` is expired.
   */
  interactive: PropTypes.bool,
  /**
   * The number of milliseconds to wait before hiding the tooltip.
   * This prop won't impact the leave touch delay (`leaveTouchDelay`).
   */
  leaveDelay: PropTypes.number,
  // The number of milliseconds after the user stops touching an element before hiding the tooltip.
  leaveTouchDelay: PropTypes.number,
  // Callback fired when the component requests to be closed
  onClose: PropTypes.func,
  // Callback fired when the component requests to be open
  onOpen: PropTypes.func,
  // If `true`, the tooltip is shown
  open: PropTypes.bool,
  // Tooltip placement
  placement: PropTypes.oneOf([
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top',
  ]),
  // The component used for the popper
  PopperComponent: PropTypes.elementType,
  // Props applid to the Popper element
  PopperProps: PropTypes.object,
  // Tooltip title. Zero-length titles string are never displayed
  title: PropTypes.node.isRequired,
  // The component used for the transition
  TransitionComponent: PropTypes.elementType,
  /**
   * Props applied to the transition element
   * By default, the element is based on the Transition component
   */
  TransitionProps: PropTypes.object,
}

export default Tooltip
